// Enderman override (MOB special, M13.2). PURE (ENG-1).
//
// A tall neutral mob that turns hostile when a player *stares* at it (a look-cone
// test) or strikes it, blinks away when hurt or wet, and occasionally carries a
// whitelisted block from one place to another (mob-griefing gated).
import { ENDERMAN, TAME } from '../../config';
import type { MobDef } from '../../registry/schemas';
import type { Entity, MobContext, PlayerHandle } from '../entity';
import { meleeAI } from '../archetypes/melee';
import { wander } from '../archetypes/shared';

/** True if `pl` is looking (roughly) straight at the enderman's head (M13.2). */
export function staringAt(pl: PlayerHandle, e: Entity): boolean {
  if (!pl.lookDir) return false;
  const look = pl.lookDir();
  const dx = e.x - pl.x, dy = e.y + 1.3 - (pl.y + pl.eyeY), dz = e.z - pl.z;
  const len = Math.hypot(dx, dy, dz) || 1;
  return (look.x * dx + look.y * dy + look.z * dz) / len > ENDERMAN.LOOK_DOT;
}

/** Blink to a random standable spot within TELEPORT_RANGE. Returns success. */
function teleport(e: Entity, ctx: MobContext): boolean {
  const { world, rng } = ctx;
  const R = ENDERMAN.TELEPORT_RANGE;
  for (let i = 0; i < ENDERMAN.TELEPORT_TRIES; i++) {
    const tx = Math.floor(e.x) + rng.nextInt(2 * R + 1) - R;
    const tz = Math.floor(e.z) + rng.nextInt(2 * R + 1) - R;
    for (let dy = -4; dy <= 4; dy++) {
      const ty = Math.floor(e.y) + dy;
      if (!world.isSolid(tx, ty, tz) && !world.isSolid(tx, ty + 1, tz) && world.isSolid(tx, ty - 1, tz)) {
        e.x = tx + 0.5; e.y = ty; e.z = tz + 0.5;
        e.vx = e.vy = e.vz = 0; e.onGround = false;
        return true;
      }
    }
  }
  return false;
}

/** Occasionally pick up a whitelisted block below, or set a carried one down. */
function carryTick(e: Entity, ctx: MobContext): void {
  const { world, rng } = ctx;
  if (!world.isCarryable || !world.placeBlock) return;
  if (rng.nextInt(ENDERMAN.CARRY_DENOM) !== 0) return;
  const fx = Math.floor(e.x), fz = Math.floor(e.z);
  if ((e.data.carry ?? 0) === 0) {
    const by = Math.floor(e.y) - 1;
    const st = world.getBlockState(fx, by, fz);
    if (st !== 0 && world.isCarryable(st)) { world.breakBlock(fx, by, fz); e.data.carry = st; }
  } else {
    const by = Math.floor(e.y);
    if (world.getBlockState(fx, by, fz) === 0 && world.isSolid(fx, by - 1, fz)) {
      world.placeBlock(fx, by, fz, e.data.carry!);
      e.data.carry = 0;
    }
  }
}

export function enderman(e: Entity, def: MobDef, ctx: MobContext): void {
  const { world, frame, sys } = ctx;

  // Water / rain scalds an enderman → damage + blink away.
  const wet = world.isFluid(Math.floor(e.x), Math.floor(e.y + 0.5), Math.floor(e.z)) || frame.raining;
  if (wet && e.age % 10 === 0) {
    sys.damage(e, ENDERMAN.WATER_DAMAGE, frame);
    teleport(e, ctx);
    if (e.removed) return;
  }

  // A player staring at it provokes it (look-cone), like being struck.
  if (e.angerTimer === 0) {
    const p = sys.nearestPlayer(frame, e.x, e.y, e.z, ENDERMAN.LOOK_RANGE);
    if (p && staringAt(frame.players[p.idx]!, e)) e.angerTimer = TAME.ANGER_TICKS;
  }

  // Blink away when freshly hurt (cooldown-gated so it doesn't strobe).
  e.data.tpCd = Math.max(0, (e.data.tpCd ?? 0) - 1);
  if (e.hurtTimer > 6 && e.data.tpCd === 0) { teleport(e, ctx); e.data.tpCd = ENDERMAN.TELEPORT_COOLDOWN; }

  if (frame.mobGriefing) carryTick(e, ctx);

  // Angry → chase and strike; otherwise amble.
  if (e.angerTimer > 0 && meleeAI(e, def, ctx)) return;
  wander(e, ctx, def);
}
